import React from "react";
import { Space } from "antd";
import {
  AxBreadcrumb,
  AxIconWrapper,
  AxDivider,
  AxButton,
} from "@dynata/axiom3";

class Breadcrumb extends React.Component {
  render() {
    return (
      <div className="section" id="breadcrumb">
        <h3>Breadcrumb</h3>
        <p>
          A breadcrumb displays the current location within a hierarchy. It
          allows going back to states higher up in the hierarchy.
        </p>

        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{`
<AxBreadcrumb>
  <AxBreadcrumb.Item>Abc</AxBreadcrumb.Item>
  <AxBreadcrumb.Item href="">
    <AxIconWrapper type="regular" size="14" icon={"fa-home"} />
  </AxBreadcrumb.Item>
  <AxBreadcrumb.Item href="">
    <AxIconWrapper type="regular" size="14" icon={"fa-home"} />
    <span>Application List</span>
  </AxBreadcrumb.Item>
  <AxBreadcrumb.Item>Application</AxBreadcrumb.Item>
</AxBreadcrumb>
`}</pre>

          <div className="prop-header">
            <p className="props-title">Props</p>
            <p>
              For AntD props, visit{" "}
              <AxButton
                size="small"
                type="link"
                target="_blank"
                href="https://ant.design/components/breadcrumb/#API"
              >
                https://ant.design/components/breadcrumbs/#API
              </AxButton>
            </p>
          </div>
          <table className="props-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Type</th>
                <th>Default</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>large</td>
                <td>boolean</td>
                <td>Render large breadcrumb</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>itemRender</td>
                <td>Custom item renderer</td>
                <td>(route, params, routes, paths) =&gt; ReactNode</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>params</td>
                <td>Routing parameters</td>
                <td>object</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>routes</td>
                <td>The routing stack information of router</td>
                <td>
                  <a href="#routes">routes[]</a>
                </td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>separator</td>
                <td>Custom separator</td>
                <td>ReactNode</td>
                <td>
                  <code>/</code>
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <div className="prop-header">
            <p className="props-title">Props: Axbreadcrumb.Item</p>
          </div>
          <table className="props-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Type</th>
                <th>Default</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>className</td>
                <td>The additional css class</td>
                <td>string</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>dropdownProps</td>
                <td>The dropdown props</td>
                <td>
                  <a href="/components/dropdown/">Dropdown</a>
                </td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>href</td>
                <td>Target of hyperlink</td>
                <td>string</td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>overlay</td>
                <td>The dropdown menu</td>
                <td>
                  <a href="/components/menu/">Menu</a> | <code>{`() =>`}</code>{" "}
                  Menu
                </td>
                <td>-</td>
                <td></td>
              </tr>
              <tr>
                <td>onClick</td>
                <td>Set the handler to handle click event</td>
                <td>
                  <code>{`(e:MouseEvent) => void`}</code>
                </td>
                <td>-</td>
                <td></td>
              </tr>

              <tr>
                <td>...others</td>
                <td>
                  All extra props are passed down to the{" "}
                  <>
                    <AxButton
                      size="small"
                      type="link"
                      target="_blank"
                      href="https://4x.ant.design/components/breadcrumb/#API"
                    >
                      {`<Breadcrumb />`}
                    </AxButton>
                  </>{" "}
                  component.
                </td>
                <td></td>
                <td>-</td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <div className="prop-header">
            <p className="props-title">Props: Axbreadcrumb.Separator</p>
          </div>
          <table className="props-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Type</th>
                <th>Default</th>
                <th>Version</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>children</td>
                <td>Custom separator</td>
                <td>ReactNode</td>
                <td>
                  <code>/</code>
                </td>
                <td></td>
              </tr>
            </tbody>
          </table>

          <AxBreadcrumb>
            <AxBreadcrumb.Item>Abc</AxBreadcrumb.Item>
            <AxBreadcrumb.Item href="">
              <AxIconWrapper type="regular" size="14" icon={"fa-home"} />
            </AxBreadcrumb.Item>
            <AxBreadcrumb.Item href="">
              <AxIconWrapper type="regular" size="14" icon={"fa-home"} />
              <span>Application List</span>
            </AxBreadcrumb.Item>
            <AxBreadcrumb.Item>Application</AxBreadcrumb.Item>
          </AxBreadcrumb>

          <AxBreadcrumb large="true">
            <AxBreadcrumb.Item>Abc</AxBreadcrumb.Item>
            <AxBreadcrumb.Item href="">
              <AxIconWrapper type="regular" size="14" icon={"fa-home"} />
            </AxBreadcrumb.Item>
            <AxBreadcrumb.Item href="">
              <AxIconWrapper type="regular" size="14" icon={"fa-home"} />
              <span>Application List</span>
            </AxBreadcrumb.Item>
            <AxBreadcrumb.Item>Application</AxBreadcrumb.Item>
          </AxBreadcrumb>
        </Space>
      </div>
    );
  }
}
export default Breadcrumb;
